import { useEffect, useState } from "react";
import { NavProps } from "./types";
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import { Theme, useTheme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import { makeStyles } from '@mui/styles';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import {withRouter} from 'react-router-dom';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Link from '@mui/material/Link';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useLocation } from 'react-router-dom'
import LaunchIcon from '@mui/icons-material/Launch';
import PolicyIcon from '@mui/icons-material/Policy';
import SignInModal from '../../components/signInModal';
import useMediaQuery from '@mui/material/useMediaQuery';
import ThemeSelector from "../themeSelector";
import Avatar from '@mui/material/Avatar';
import { useAuth, useModal, useAvatar } from '../../hooks';

export const drawerWidth = 240;
export const drawerBreakpoint = "md";
export const drawerBreakpointUp = "mdUp";
export const drawerBreakpointDown = "sm";

interface MyStyles {
  navContainer: string;
  stickToBottom: string;
  bottomMargin: string;
  drawerPaper: string;
  drawer: string;
  showWhenSmall: string;
  hideWhenSmall: string;
  drawerHeader: string;
}

const useStyles = makeStyles((theme: Theme) => 
  createStyles({
      navContainer: {
        width:'100vw',
        maxWidth:'inherit'
      },
      stickToBottom: {
        position: 'fixed',
        bottom: 0,
        width:'inherit',
        maxWidth:'inherit'
      },
      bottomMargin: {
        height: '60px',
      },
      drawerPaper: {
        width: drawerWidth,
      },
      drawer: {
        ...(theme?.breakpoints?.up && {
          [theme.breakpoints.up(drawerBreakpoint)]: {
            width: drawerWidth,
            flexShrink: 0,
          },
        }),
      },
      showWhenSmall: {
        ...(theme?.breakpoints?.up && {
          [theme.breakpoints.up(drawerBreakpoint)]: {
            display: 'none',
          },
        }),
      },
      hideWhenSmall: {
        ...(theme?.breakpoints?.down && {
          [theme.breakpoints.down(drawerBreakpointDown)]: {
            display: 'none',
          },
        }),
      },
      drawerHeader: {
        alignItems: 'center',
        padding: theme?.spacing ? theme.spacing(0, 1) : '0 8px',
        justifyContent: 'flex-end',
        ...(theme?.breakpoints?.down && {
          [theme.breakpoints.down(drawerBreakpointDown)]: {
            display: 'flex',
          },
        }),
        ...(theme?.breakpoints?.up && {
          [theme.breakpoints.up(drawerBreakpoint)]: {
            display: 'none',
          },
        }),
      },
  })
);

const nav = function Nav(props: NavProps) {
    const theme = useTheme();
    const location = useLocation();

    // Use our new state management hooks
    const { isAuthenticated, signOut } = useAuth();
    const signInModal = useModal('signIn');
    
    // Extract stable references to prevent infinite loops
    const openSignInModal = signInModal.open;
    const closeSignInModal = signInModal.close;
    const isSignInModalOpen = signInModal.isOpen;
    
    // Local state for drawer (this is UI-specific, not global state)
    const [mobileOpen, setMobileOpen] = useState(false);

    const NavComponent = () => {
      const classes = useStyles() as MyStyles;
      
      // Replace Hidden component with useMediaQuery to fix infinite loop
      const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
      const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

      const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
      };
  
      const navigateTo = function(path: string) {
        setMobileOpen(false);
        props.history.push(path);
      }

      const openInNewTab = (url: string) => {
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
      };
  
      const handleSignOut = async function() {
        setMobileOpen(false);
        try {
          await signOut();
          navigateTo("/");
        } catch (error) {
            console.log('error signing out: ', error);
        }
      }
  
      const renderDrawer = function () {
        return (
          <div>
            <div className={classes.drawerHeader}>
              <IconButton onClick={handleDrawerToggle}>
                {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
              </IconButton>
            </div>
          <Divider />
            <List>
              <ListItem button onClick={()=>navigateTo("/")}>
                <ListItemIcon><HomeIcon /></ListItemIcon>
                <ListItemText primary="Home" />
              </ListItem>
              <ListItem button onClick={()=>navigateTo("/profile")}>
                <ListItemIcon><PersonIcon /></ListItemIcon>
                <ListItemText primary="Edit Profile" />
              </ListItem>
              <ListItem button onClick={()=>navigateTo("/contact-us")}>
                <ListItemIcon><ContactSupportIcon /></ListItemIcon>
                <ListItemText primary="Contact Us" />
              </ListItem>
              <ListItem button onClick={()=>props.onThemeChange()}>
                <ListItemIcon><ThemeSelector colorMode={props.colorMode} /></ListItemIcon>
                <ListItemText primary="Dark/Light Mode" />
              </ListItem>
              <ListItem button onClick={handleSignOut}>
                <ListItemIcon><ExitToAppIcon /></ListItemIcon>
                <ListItemText primary="Sign Out" />
              </ListItem>
              <Divider />
              <ListItem 
                button
                component={Link} 
                href="https://www.iubenda.com/privacy-policy/15440783"
                color="textPrimary"
                target="_blank"
              >
                <ListItemIcon><PolicyIcon /></ListItemIcon>
                <ListItemText primary="Privacy Policy" />
              </ListItem>
              <ListItem 
                button
                component={Link} 
                href="https://www.iubenda.com/privacy-policy/15440783/cookie-policy"
                color="textPrimary"
                target="_blank"
              >
                <ListItemIcon><PolicyIcon /></ListItemIcon>
                <ListItemText primary="Cookie Policy" />
              </ListItem>
              <ListItem 
                button
                component={Link} 
                href="https://www.iubenda.com/terms-and-conditions/15440783"
                color="textPrimary"
                target="_blank"
              >
                <ListItemIcon><PolicyIcon /></ListItemIcon>
                <ListItemText primary="Terms and Conditions" />
              </ListItem>
            </List>
          </div>
        );
      }
  
      const renderNavForLoggedInState = () => {
          return (
            <div className={classes.navContainer}>
              <BottomNavigation className={classes.stickToBottom} showLabels>
                {!isMdUp && (
                  <BottomNavigationAction
                    icon={<MoreHorizIcon />}
                    onClick={handleDrawerToggle}
                  />
                )}
                <BottomNavigationAction icon={<Avatar src={process.env.PUBLIC_URL + '/unity_bead_icon.png'}/>} onClick={()=>openInNewTab("https://kandilove.com/collections/unity-beads")} />
                <BottomNavigationAction data-testid="bottom-navigation-home-button" label="Home" icon={<HomeIcon />} onClick={()=>navigateTo("/")} />
                <BottomNavigationAction label="Profile" icon={<PersonIcon />} onClick={()=>navigateTo("/profile")} />
                <BottomNavigationAction 
                  className={classes.hideWhenSmall}
                  label="Log Out" 
                  icon={<ExitToAppIcon />}
                  onClick={handleSignOut} 
                />
              </BottomNavigation>
              <nav className={classes.drawer} aria-label="application menu">
                {!isMdUp && (
                  <Drawer
                    variant="temporary"
                    anchor={theme.direction === 'rtl' ? 'right' : 'left'}
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    classes={{
                      paper: classes.drawerPaper,
                    }}
                    ModalProps={{
                      keepMounted: true, // Better open performance on mobile.
                    }}
                  >
                    {renderDrawer()}
                  </Drawer>
                )}
                {!isSmDown && (
                  <Drawer
                    classes={{
                      paper: classes.drawerPaper,
                    }}
                    variant="permanent"
                    open
                  >
                    {renderDrawer()}
                  </Drawer>
                )}
              </nav>
            </div>
          );
      }
  
      const renderNavForLoggedOutState = () => {
        if (location.pathname === "/") {
          return (
            <span></span>
          );
        } else {
          return (
            <div className={classes.navContainer}>
              <BottomNavigation className={`${classes.stickToBottom} ${classes.showWhenSmall}`} showLabels>
                <BottomNavigationAction label="Home" icon={<HomeIcon />} onClick={()=>navigateTo("/")} />
                <BottomNavigationAction
                  icon={<LaunchIcon />}
                  onClick={openSignInModal}
                  label="Sign In / Sign Up"
                />
              </BottomNavigation>
              <nav className={classes.drawer} aria-label="application menu">
                {!isSmDown && (
                  <Drawer
                    classes={{
                      paper: classes.drawerPaper,
                    }}
                    variant="permanent"
                    open
                  >
                    <List>
                      <ListItem button onClick={()=>navigateTo("/")}>
                        <ListItemIcon><HomeIcon /></ListItemIcon>
                        <ListItemText primary="Home" />
                      </ListItem>
                      <ListItem button onClick={openSignInModal}>
                        <ListItemIcon><LaunchIcon /></ListItemIcon>
                        <ListItemText primary="Sign In / Sign Up" />
                      </ListItem>
                    </List>
                  </Drawer>
                )}
              </nav>
              <SignInModal 
                  onClose={closeSignInModal}
                  open={isSignInModalOpen}
              />
            </div>
          )
        }
      }

      return isAuthenticated ? renderNavForLoggedInState() : renderNavForLoggedOutState();
    }

    return (
      <NavComponent />
    );
  }

  export default withRouter(nav);
